import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  buildLinkAnalysisDossierPdfDefinition,
  DossierEmbeddedImage,
  DossierImageSource,
  LinkAnalysisDossierPdfInput,
} from './link-analysis-pdf-export.util';

@Injectable({
  providedIn: 'root',
})
export class LinkAnalysisExportService {
  async exportDossierPdf(input: LinkAnalysisDossierPdfInput): Promise<void> {
    const linkAnalysisId = String(input.investigation?.link_analysis_id || 'link-analysis').trim();
    const fileName = `osint-dossier-${linkAnalysisId}.pdf`;

    this.ensurePdfMakeFonts();
    const docDefinition = buildLinkAnalysisDossierPdfDefinition(input);

    return new Promise((resolve, reject) => {
      try {
        const pdf = pdfMake.createPdf(docDefinition);
        pdf.download(fileName, () => resolve());
      } catch (error) {
        reject(error);
      }
    });
  }

  async embedImagesForPdf(sources: DossierImageSource[]): Promise<DossierEmbeddedImage[]> {
    const embedded: DossierEmbeddedImage[] = [];

    for (const source of sources) {
      const dataUrl = await this.fetchImageDataUrl(source.url);
      if (dataUrl) {
        embedded.push({ label: source.label, dataUrl });
      }
    }

    return embedded;
  }

  private fetchImageDataUrl(url: string): Promise<string | null> {
    return new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        try {
          const maxWidth = 500;
          const scale = Math.min(1, maxWidth / Math.max(image.width, 1));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.width * scale));
          canvas.height = Math.max(1, Math.round(image.height * scale));
          const context = canvas.getContext('2d');
          if (!context) {
            resolve(null);
            return;
          }
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch {
          resolve(null);
        }
      };
      image.onerror = () => resolve(null);
      image.src = url;
    });
  }

  private ensurePdfMakeFonts(): void {
    const pdfMakeAny: any = pdfMake as any;
    const fontsAny: any = pdfFonts as any;

    const vfs =
      pdfMakeAny.vfs ||
      fontsAny?.pdfMake?.vfs ||
      fontsAny?.vfs ||
      fontsAny?.default ||
      fontsAny;

    if (vfs && typeof vfs === 'object' && vfs['Roboto-Regular.ttf']) {
      pdfMakeAny.vfs = vfs;
    }

    if (typeof pdfMakeAny.addVirtualFileSystem === 'function' && vfs) {
      pdfMakeAny.addVirtualFileSystem(vfs);
    }
  }
}
